import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Flame, RefreshCw, Star } from 'lucide-react';
import Navigation from '@/components/Navigation';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROP_TYPES = ['All', 'Points', 'Rebounds', 'Assists', 'Threes', 'Blocks', 'Steals'];

const WnbaHotProps = () => {
  const [props, setProps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filterPropType, setFilterPropType] = useState('All');
  const [filterConfidence, setFilterConfidence] = useState('all');

  const fetchProps = async () => {
    setRefreshing(true);
    try {
      const res = await axios.get(`${API}/wnba/hot-props`);
      setProps(res.data.suggestions || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); setRefreshing(false); }
  };

  useEffect(() => { fetchProps(); }, []);

  const filtered = props.filter(p => {
    const mt = filterPropType === 'All' || p.prop_type === filterPropType;
    const mc = filterConfidence === 'all' || p.confidence === filterConfidence;
    return mt && mc;
  });

  const confColor = (c) => c === 'high' ? 'bg-green-500/20 text-green-400 border-green-500/30'
    : c === 'medium' ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
    : 'bg-gray-500/20 text-gray-400 border-gray-500/30';

  return (
    <div className="min-h-screen bg-[#0a1628]">
      <Navigation />
      <main className="pt-20 pb-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between gap-4 mb-8 flex-wrap">
            <div>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white uppercase tracking-tight flex items-center gap-3">
                <Flame className="h-8 w-8 text-orange-500" />
                WNBA Hot Props
              </h1>
              <p className="text-muted-foreground mt-1">Top players for Points, Rebounds, Assists, Threes, Blocks & Steals</p>
            </div>
            <Button onClick={fetchProps} variant="outline" className="border-[#1e3a5f] text-white hover:bg-[#112240]" disabled={refreshing} data-testid="refresh-wnba-props-btn">
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {/* Filters */}
          <div className="flex flex-col gap-3 mb-6">
            <div className="flex flex-wrap gap-2">
              {PROP_TYPES.map(pt => (
                <Button
                  key={pt}
                  variant={filterPropType === pt ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterPropType(pt)}
                  className={filterPropType === pt
                    ? 'bg-brand-lime text-[#0a1628] hover:bg-brand-lime/90'
                    : 'border-[#1e3a5f] text-white hover:bg-[#112240]'}
                  data-testid={`wnba-prop-type-filter-${pt.toLowerCase()}`}
                >
                  {pt}
                </Button>
              ))}
            </div>
            <div className="flex gap-2">
              {['all', 'high', 'medium'].map(c => (
                <Button
                  key={c}
                  variant={filterConfidence === c ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterConfidence(c)}
                  className={filterConfidence === c
                    ? (c === 'high' ? 'bg-green-500 text-white' : c === 'medium' ? 'bg-yellow-500 text-[#0a1628]' : 'bg-brand-cyan text-[#0a1628]')
                    : 'border-[#1e3a5f] text-white hover:bg-[#112240]'}
                  data-testid={`wnba-confidence-filter-${c}`}
                >
                  {c === 'all' ? 'All' : c === 'high' ? <><Star className="h-4 w-4 mr-1" />High Confidence</> : 'Medium'}
                </Button>
              ))}
            </div>
          </div>

          <div className="mb-6 flex items-center gap-2">
            <Badge variant="outline" className="border-brand-cyan text-brand-cyan">{filtered.length} Props Available</Badge>
            <span className="text-muted-foreground text-sm">• League leaders by per-game average</span>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map(i => <div key={i} className="h-52 skeleton rounded-lg" />)}
            </div>
          ) : filtered.length === 0 ? (
            <Card className="bg-[#0f1e36] border-[#1e3a5f]">
              <CardContent className="p-12 text-center text-muted-foreground">No props match your filters.</CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((p, idx) => (
                <Card key={p.id} className="bg-[#0f1e36] border-[#1e3a5f] card-hover stat-card animate-slide-up" style={{ animationDelay: `${idx * 0.03}s` }} data-testid={`wnba-hot-prop-card-${idx}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4 mb-4">
                      <img
                        src={p.headshot}
                        alt={p.player_name}
                        className="w-16 h-16 rounded-full bg-[#112240] object-cover border-2 border-orange-400"
                        onError={(e) => { e.target.src = 'https://a.espncdn.com/combiner/i?img=/i/headshots/nophoto.png&w=200&h=146'; }}
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xl">🏀</span>
                          <span className="sport-icon sport-nba text-[10px]">{p.team}</span>
                        </div>
                        <h3 className="font-semibold text-white text-lg">{p.player_name}</h3>
                        <p className="text-sm text-muted-foreground">{p.prop_type}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mb-4">
                      <Badge variant="outline" className="border-orange-400 text-orange-400">#{p.rank} in {p.prop_type}</Badge>
                      <span className={`px-3 py-1 rounded-full text-xs font-bold border ${confColor(p.confidence)}`}>
                        {p.confidence.toUpperCase()}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="bg-[#0a1628] rounded p-2 text-center">
                        <div className="text-xs text-muted-foreground">{p.prop_type}/Game</div>
                        <div className="text-brand-lime font-mono font-bold text-lg">{p.avg}</div>
                      </div>
                      <div className="bg-[#0a1628] rounded p-2 text-center">
                        <div className="text-xs text-muted-foreground">Games Played</div>
                        <div className="text-brand-cyan font-mono font-bold text-lg">{p.games ?? '—'}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default WnbaHotProps;
